/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { showToast } from "components/AppToast"
import { CurrentUserParameters } from "features/userParameters/UserParametersSchemata"
import { useState } from "react"
import { FieldErrors, useFormContext } from "react-hook-form"
import { Sentry } from "services/sentry"
import { downloadWorkbook } from "services/workbooks/v2/workbookBuilding"
import { countTotalErrors, navigateToFirstErrorTab } from "utils/form/tabErrors"

// AIDEV-NOTE: Keep submit-handler inputs readonly-safe at boundary points without changing download/validation flow.
const XlsxDownload = () => {
  const { handleSubmit } = useFormContext<CurrentUserParameters>()
  const [isDownloading, setIsDownloading] = useState(false)
  const [_downloadError, setDownloadError] = useState<string | null>(null)

  const onSuccess = (dataInput: unknown) => {
    const data = dataInput as CurrentUserParameters
    try {
      downloadWorkbook(data)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error creating file"
      setDownloadError(errorMessage)

      // Report download errors for system monitoring (browser API failures, memory issues, etc.)
      Sentry.captureException(error instanceof Error ? error : new Error(errorMessage), {
        tags: { errorType: "file_download_error", component: "XlsxDownload" },
      })

      showToast({
        type: "error",
        title: "File Save Error",
        message: errorMessage,
      })
    } finally {
      setIsDownloading(false)
    }
  }

  // The error handler when validation fails
  const onError = (errorsInput: unknown) => {
    const errors = errorsInput as FieldErrors<CurrentUserParameters>
    // Use the navigateToFirstErrorTab function, which will handle the errorMap creation
    // and finding the first tab with errors for us
    const errorMap = navigateToFirstErrorTab(errors, tabId => {
      const tabButton = document.getElementById(tabId)

      if (tabButton && tabButton instanceof HTMLElement) {
        tabButton.click()
      }
    })

    // Count total errors
    const errorCount = countTotalErrors(errorMap)
    const errorMessage = `Please fix ${errorCount} ${errorCount === 1 ? "error" : "errors"} before saving the file.`

    setDownloadError(errorMessage)

    showToast({
      type: "error",
      title: "Input Validation Error",
      message: errorMessage,
    })

    setIsDownloading(false)
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    setDownloadError(null)

    // Trigger validation and handle the form submission just like Calculate button does
    await handleSubmit(onSuccess, onError)()
  }

  return (
    <div>
      <div className="d-flex align-items-center">
        <button
          type="button"
          className="btn btn-secondary mt-3"
          onClick={() => {
            void handleDownload()
          }}
          disabled={isDownloading}
        >
          Save
        </button>

        {isDownloading && (
          <div className="spinner-border spinner-border-sm ms-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default XlsxDownload
